import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { Button, ScreenLoader } from '../../components/design-system';
import { readPendingTryChat } from '../../lib/tryChatHandoff';

/**
 * Landing page for every web auth redirect: OAuth, email confirmation, and
 * recovery links.
 *
 * supabase-js (detectSessionInUrl + PKCE) exchanges a `?code=` on load, but
 * ONLY when the PKCE code_verifier is in this browser's localStorage — open a
 * confirmation email on a different device and it silently does nothing. So we
 * also handle `token_hash` (verifyOtp, which works anywhere) and fall back to
 * an explicit exchange, surfacing a real message instead of a blank bounce.
 */
export function AuthCallback() {
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    let settled = false;
    let timer: ReturnType<typeof setTimeout>;

    const params = new URLSearchParams(window.location.search);

    // Google OAuth from the /try chat's auth modal does a full-page redirect
    // through here, unmounting TryChat before it can save the conversation.
    // Route back to /try when a draft is pending so its resume effect can
    // pick it up — that's what runs the extract + review-modal flow, same as
    // the in-modal email/password path. Don't finalize here: doing so would
    // skip straight to /matches and bypass the review step entirely.
    async function goAfterSession() {
      if (settled || cancelled) return;
      settled = true; // claim immediately: the listener and the checks below can both fire
      clearTimeout(timer);
      sub.subscription.unsubscribe();
      // A password-recovery link should land on the "set a new password"
      // screen, not straight into the app — the session it just established
      // exists only to authorize that one change.
      if (params.get('type') === 'recovery') {
        if (!cancelled) navigate('/reset-password', { replace: true });
        return;
      }
      const target = readPendingTryChat() ? '/try' : '/';
      if (!cancelled) navigate(target, { replace: true });
    }

    function fail(message: string) {
      if (settled || cancelled) return;
      settled = true;
      clearTimeout(timer);
      sub.subscription.unsubscribe();
      setError(message);
    }

    // Subscribe FIRST so we can't miss the session landing between the listener
    // setup and the checks below.
    const { data: sub } = supabase.auth.onAuthStateChange((_event, session) => {
      if (session) void goAfterSession();
    });

    void (async () => {
      // The provider/Supabase told us outright that something went wrong.
      const described = params.get('error_description');
      if (described) { fail(described); return; }

      // Already signed in (PKCE exchange may have completed on load).
      const { data: existing } = await supabase.auth.getSession();
      if (existing.session) { void goAfterSession(); return; }

      // Email confirmation / recovery via token_hash — works on any device.
      const tokenHash = params.get('token_hash');
      const type = params.get('type');
      if (tokenHash && type) {
        const { error: otpError } = await supabase.auth.verifyOtp({
          token_hash: tokenHash,
          type: type as 'signup' | 'email' | 'recovery' | 'invite' | 'email_change',
        });
        if (otpError) {
          fail(`${otpError.message}. The link may have expired — request a new one.`);
        }
        // Success fires onAuthStateChange, which routes onward.
        return;
      }

      // A `?code=` that detectSessionInUrl didn't pick up: try explicitly, so
      // the failure reason is visible rather than an endless spinner.
      const code = params.get('code');
      if (code) {
        const { error: exchangeError } = await supabase.auth.exchangeCodeForSession(code);
        if (exchangeError) {
          fail(
            'This link couldn\'t be completed in this browser. Open it in the same browser you signed up from, or just sign in with your email and password.',
          );
        }
        return;
      }

      // Nothing actionable in the URL — wait briefly for a session, then bail.
      timer = setTimeout(() => fail('We couldn\'t complete sign-in. Please try signing in again.'), 15000);
    })();

    return () => {
      cancelled = true;
      clearTimeout(timer);
      sub.subscription.unsubscribe();
    };
  }, [navigate]);

  if (error) {
    return (
      <div className="min-h-dvh bg-app-bg flex items-center justify-center px-4">
        <div className="w-full max-w-md bg-app-card border border-app-border rounded-2xl p-8 text-center">
          <h2 className="text-xl font-semibold text-app-fg mb-2">Sign-in didn't complete</h2>
          <p className="text-sm text-app-muted leading-relaxed mb-6">{error}</p>
          <Button size="lg" className="w-full" onClick={() => navigate('/login', { replace: true })}>
            Go to sign in
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-dvh bg-app-bg flex items-center justify-center">
      <ScreenLoader />
    </div>
  );
}

export default AuthCallback;
